// ============================================================================
// CrewAhead Maritime Platform - API Response Transform Utilities
// ============================================================================
// Django REST Framework returns snake_case field names, but our frontend
// TypeScript types use camelCase. These utilities bridge the gap.

// ─── Snake Case → Camel Case ─────────────────────────────────────────────────

/**
 * Converts a snake_case string to camelCase.
 */
function toCamelCase(str: string): string {
return str.replace(/_([a-z0-9])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Recursively transforms all object keys from snake_case to camelCase.
 * Handles nested objects, arrays, and primitive values.
 * Skips Date objects and null/undefined values.
 */
function transformKeys<T>(obj: unknown): T {
  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  // Handle Date objects
  if (obj instanceof Date) {
    return obj as T;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys<unknown>(item)) as T;
  }

  // Handle objects
  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = toCamelCase(key);
      result[camelKey] = transformKeys<unknown>(value);
    }
    return result as T;
  }

  // Primitives (string, number, boolean)
  return obj as T;
}

// ─── Axios Response Transformer ──────────────────────────────────────────────

/**
 * Transforms a DRF snake_case response into camelCase.
 * Use this in the axios response interceptor to normalize all API data.
 *
 * @param data - Raw API response data (snake_case)
 * @returns Transformed data (camelCase)
 *
 * @example
 * ```ts
 * // Raw DRF response:
 * // { current_rank: "chief_officer", years_of_experience: 5, user: { first_name: "John" } }
 *
 * const transformed = camelCaseKeys(rawData);
 * // { currentRank: "chief_officer", yearsOfExperience: 5, user: { firstName: "John" } }
 * ```
 */
export function camelCaseKeys<T>(data: unknown): T {
  return transformKeys<T>(data);
}

/**
 * Selectively transforms specific fields from snake_case to camelCase.
 * Use this when you only need to transform certain fields and want to
 * avoid the overhead of deep transformation.
 */
export function camelCaseFields<T extends Record<string, unknown>>(
  obj: Record<string, unknown>,
  fieldMapping: Record<string, string>
): T {
  const result: Record<string, unknown> = { ...obj };
  for (const [snakeKey, camelKey] of Object.entries(fieldMapping)) {
    if (snakeKey in obj) {
      result[camelKey] = obj[snakeKey];
    }
  }
  return result as T;
}

// ─── Camel Case → Snake Case (for outgoing requests) ─────────────────────────

/**
 * Converts a camelCase string to snake_case.
 */
function toSnakeCase(str: string): string {
  return str
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '');
}

/**
 * Recursively transforms all object keys from camelCase to snake_case.
 * Handles nested objects, arrays, and primitive values.
 * Skips Date objects and null/undefined values.
 */
function transformKeysToSnake<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (obj instanceof Date) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToSnake<unknown>(item)) as T;
  }

  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = toSnakeCase(key);
      result[snakeKey] = transformKeysToSnake<unknown>(value);
    }
    return result as T;
  }

  return obj as T;
}

/**
 * Transforms request payload keys from camelCase to snake_case.
 * Use before sending data to Django REST Framework backend.
 *
 * @example
 * ```ts
 * const payload = { firstName: 'John', lastName: 'Doe', companyName: 'Acme' };
 * const body = snakeCaseKeys(payload);
 * // { first_name: 'John', last_name: 'Doe', company_name: 'Acme' }
 * ```
 */
export function snakeCaseKeys<T>(data: unknown): T {
  return transformKeysToSnake<T>(data);
}

// ─── Pagination Result Extractor ─────────────────────────────────────────────

/**
 * Extracts results from a DRF paginated response.
 * Handles both paginated ({ count, next, previous, results }) and
 * non-paginated (plain array) responses.
 */
export function extractResults<T>(response: unknown): T[] {
  if (response && typeof response === 'object' && 'results' in response) {
    const paginated = response as { results: T[] };
    return Array.isArray(paginated.results) ? paginated.results : [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
}

/**
 * Extracts pagination metadata from a DRF paginated response.
 * Returns null for non-paginated responses.
 */
export function extractPaginationMeta(response: unknown): {
  count: number;
  next: string | null;
  previous: string | null;
} | null {
  if (
    response &&
    typeof response === 'object' &&
    'count' in response &&
    'results' in response
  ) {
    const paginated = response as unknown as {
      count: number;
      next: string | null;
      previous: string | null;
    };
    return {
      count: paginated.count,
      next: paginated.next,
      previous: paginated.previous,
    };
  }
  return null;
}
